import axios from "axios";

// P4 (Juni 2026) – Betreiber-Konsole: DEDIZIERTER API-Client.
//
// Bewusst NICHT der geteilte `@/lib/api`-`client`: dessen Interceptor hängt auf
// (fast) allen Routen automatisch den OWNER-Token an (routeKind-Default =
// "owner"). Die Ops-Endpunkte brauchen aber den separaten OPS_API_TOKEN. Dieser
// Client ist daher vollständig isoliert vom Owner-/Employee-Session-Pfad.
//
// Token-Haltung: ausschließlich `sessionStorage` (kein localStorage, nie in der
// URL, nie geloggt). Bei Tab-Reload bleibt er für die Session erhalten; ein
// 401 (abgelaufen/ungültig) führt im UI zur erneuten Eingabe.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ops_token";

export function getOpsToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setOpsToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sessionStorage optional */
  }
}

export function clearOpsToken() {
  setOpsToken("");
}

const opsClient = axios.create({ baseURL: API });

opsClient.interceptors.request.use((config) => {
  const token = getOpsToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const opsApi = {
  // Reichhaltige Liste + globale Totals (P3-Endpoint) – Datenquelle für Liste UND KPIs.
  // OPS-504-FIX (2026-08) – reiner Auth-Check (require_ops_token, KEINE DB):
  // entscheidet ALLEIN über `authed`; tenant-metrics ist nur noch Datenpfad.
  authCheck: () => opsClient.get("/ops/auth/check").then((r) => r.data),
  tenantMetrics: () => opsClient.get("/ops/tenant-metrics").then((r) => r.data),
  // P2.4a – Dashboard (read-only): reine Komposition vorhandener Aggregatoren.
  dashboardSummary: () => opsClient.get("/ops/dashboard/summary").then((r) => r.data),
  // DEPLOY-01 (Betreiberfreigabe 14.10., R3): read-only Startup-Status des laufenden Prozesses.
  startupStatus: () => opsClient.get("/ops/startup-status").then((r) => r.data),
  // P2.1 – read-only Mandanten-Detailansicht (Stamm/MA/Welten/Übersetzungen/KI-Nutzung).
  tenantDetail: (tid) => opsClient.get(`/ops/tenants/${tid}/detail`).then((r) => r.data),
  // WELT-02b (Betreiberfreigabe 12.10.): read-only Zusatzkontingent + Paketbuchungen.
  tenantWeltenPaket: (tid) =>
    opsClient.get(`/ops/tenants/${tid}/welten-paket`).then((r) => r.data),
  // MOD-01b-4 (Betreiberfreigabe 13.10.): read-only Modulstatus (aktiv + Quelle) je Mandant,
  // getrennt von tenant-metrics; read-only Abrechnungssicht je Mandant (Zeiträume, Nachberechnung,
  // Modulbuchungen; Endpunkt aus MOD-01b-2a/2b).
  moduleAccess: () => opsClient.get("/ops/module-access").then((r) => r.data),
  tenantBillingPeriods: (tid) =>
    opsClient.get(`/ops/tenants/${tid}/billing-periods`).then((r) => r.data),
  // P2.2 – Übersetzungszentrum (read-only): globale KPIs, je Mandant, Fehler, Jobs.
  translationOverview: () => opsClient.get("/ops/translations/overview").then((r) => r.data),
  translationTenants: () => opsClient.get("/ops/translations/tenants").then((r) => r.data),
  translationErrors: (limit) =>
    opsClient
      .get("/ops/translations/errors", { params: limit ? { limit } : {} })
      .then((r) => r.data),
  translationJobs: (params) =>
    opsClient.get("/ops/translations/jobs", { params: params || {} }).then((r) => r.data),
  // P2.3a – Verbrauchszentrum (read-only, KEINE Euro): Tokens/Aufrufe je Mandant/Modell/Art.
  usageOverview: (range) =>
    opsClient.get("/ops/usage/overview", { params: range ? { range } : {} }).then((r) => r.data),
  usageTenants: (range) =>
    opsClient.get("/ops/usage/tenants", { params: range ? { range } : {} }).then((r) => r.data),
  usageModels: (range) =>
    opsClient.get("/ops/usage/models", { params: range ? { range } : {} }).then((r) => r.data),
  usageKinds: (range) =>
    opsClient.get("/ops/usage/kinds", { params: range ? { range } : {} }).then((r) => r.data),
  // P2.3b-1 – Kostenzentrum (read-only, abgeleitet aus price_table; KEINE Marge).
  costOverview: (range) =>
    opsClient.get("/ops/costs/overview", { params: range ? { range } : {} }).then((r) => r.data),
  costTenants: (range) =>
    opsClient.get("/ops/costs/tenants", { params: range ? { range } : {} }).then((r) => r.data),
  costModels: (range) =>
    opsClient.get("/ops/costs/models", { params: range ? { range } : {} }).then((r) => r.data),
  costTimeseries: (range, granularity) =>
    opsClient
      .get("/ops/costs/timeseries", { params: { ...(range ? { range } : {}), granularity: granularity || "day" } })
      .then((r) => r.data),
  createTenant: (payload) => opsClient.post("/ops/tenants", payload).then((r) => r.data),
  extend: (tid, days) =>
    opsClient.post(`/ops/tenants/${tid}/extend`, { days }).then((r) => r.data),
  suspend: (tid) => opsClient.post(`/ops/tenants/${tid}/suspend`).then((r) => r.data),
  activate: (tid) => opsClient.post(`/ops/tenants/${tid}/activate`).then((r) => r.data),
  upgrade: (tid) => opsClient.post(`/ops/tenants/${tid}/upgrade`).then((r) => r.data),
  factoryReset: (tid) =>
    opsClient.post(`/ops/tenants/${tid}/factory-reset`).then((r) => r.data),
  // Owner-PIN-Reset (2026-09): Kundenfall „PIN vergessen". confirm_company_code
  // ist der Fehlbedien-Schutz des Backends (400 bei Mismatch, kein Write).
  resetOwnerPin: (tid, confirmCompanyCode) =>
    opsClient
      .post(`/ops/tenants/${tid}/reset-owner-pin`, {
        confirm_company_code: confirmCompanyCode,
      })
      .then((r) => r.data),
  // Sprach-Entitlement (Juni 2026): ersetzt die Override-Liste KOMPLETT; das
  // Backend reiht für NEU hinzugekommene Sprachen einen kostenpflichtigen
  // Bestands-Backfill ein. Firmencode-Schutz liegt clientseitig im Dialog.
  setLanguageEntitlement: (tid, payload) =>
    opsClient
      .post(`/ops/tenants/${tid}/language-entitlement`, payload)
      .then((r) => r.data),
  // P9.3 (Juni 2026) – Tarifwechsel-Anfragen (read + Statuspflege OHNE Tarifwirkung).
  planChangeRequests: (status) =>
    opsClient
      .get("/ops/plan-change-requests", { params: status ? { status } : {} })
      .then((r) => r.data),
  setPlanChangeStatus: (id, status, note) =>
    opsClient
      .post(`/ops/plan-change-requests/${id}/status`, { status, note })
      .then((r) => r.data),
  // M3.1 (Juni 2026) – Shadow-Validation (READ-ONLY): Cutover-Kennzahlen + Detailtabelle.
  shadowSummary: () =>
    opsClient.get("/ops/shadow-validation/summary").then((r) => r.data),
  shadowDetails: (filter) =>
    opsClient
      .get("/ops/shadow-validation/details", { params: filter ? { filter } : {} })
      .then((r) => r.data),
  // M4.5 (Juni 2026) – OPS Contract Preview (READ-ONLY): wie der Resolver jeden Mandanten parst.
  contractsPreview: (filter) =>
    opsClient
      .get("/ops/contracts/preview", { params: filter ? { filter } : {} })
      .then((r) => r.data),
  // ADR-007 B (Juli 2026) – kontrollierte Nacharchivierung (Preview read-only,
  // Requeue nur mit confirm+reason, Abschlussprüfung schreibt das Abschluss-Audit).
  rearchivePreview: (tid) =>
    opsClient.get(`/ops/tenants/${tid}/rearchive-preview`).then((r) => r.data),
  rearchiveStart: (tid, reason) =>
    opsClient
      .post(`/ops/tenants/${tid}/rearchive`, { confirm: true, reason })
      .then((r) => r.data),
  rearchiveComplete: (tid) =>
    opsClient.post(`/ops/tenants/${tid}/rearchive-complete`).then((r) => r.data),
  // N2A (Juni 2026) – Sales-Katalog (STRIKT READ-ONLY): Versionsliste + Versionsdetail.
  // Nur die zwei bestehenden GET-Endpunkte; KEINE Draft-/Publish-/Schreibfunktionen.
  salesCatalogVersions: () =>
    opsClient.get("/ops/sales-catalog/versions").then((r) => r.data),
  salesCatalogVersion: (versionId) =>
    opsClient
      .get(`/ops/sales-catalog/versions/${encodeURIComponent(versionId)}`)
      .then((r) => r.data),
  // N2C (Juni 2026) – Sales-Katalog: EINZIGE Schreibaktion — Draft anlegen über den
  // bestehenden, write-gate-geschützten Endpunkt (fail-closed 503; zweiter Draft → 409).
  // KEIN Publish, KEINE Bearbeitung, KEINE Löschung.
  // O1G (Juli 2026): Der Payload enthält KEINE version_id mehr — die neue
  // Versions-ID (YYYY-MM-DD-NNNN) vergibt ausschließlich der Server.
  salesCatalogCreateDraft: (payload) =>
    opsClient.post("/ops/sales-catalog/draft", payload).then((r) => r.data),
  // N2E3 (Juli 2026) – Sales-Katalog: Anbindung des bestehenden N2E2-Speicherpfads
  // PUT /ops/sales-catalog/draft/{version_id} (CAS via expected_content_hash,
  // vollständige Produktliste, draft-only). KEIN Retry, keine neue Tokenlogik.
  salesCatalogUpdateDraft: (versionId, payload) =>
    opsClient
      .put(`/ops/sales-catalog/draft/${encodeURIComponent(versionId)}`, payload)
      .then((r) => r.data),
  // N2F3 (Juli 2026) – Sales-Katalog: Anbindung des in N2F2 gehärteten Publish-Endpunkts
  // POST /ops/sales-catalog/draft/{version_id}/publish. Der Body enthält AUSSCHLIESSLICH
  // den expected_content_hash des aktuell geladenen Drafts (CAS). KEIN Retry,
  // Backend-Fehlermeldungen werden unverändert durchgereicht.
  salesCatalogPublishDraft: (versionId, expectedContentHash) =>
    opsClient
      .post(`/ops/sales-catalog/draft/${encodeURIComponent(versionId)}/publish`, {
        expected_content_hash: expectedContentHash,
      })
      .then((r) => r.data),
  // O1E (Juli 2026) – Sales-Katalog: Anbindung des in O1D implementierten
  // Produktanlage-Endpunkts POST /ops/sales-catalog/draft/{version_id}/products
  // (CAS via expected_content_hash + GENAU EIN vollständiges 28-Feld-Produkt).
  // KEIN Retry, keine neue Tokenlogik, Backend-Fehler unverändert durchgereicht.
  salesCatalogAddDraftProduct: (versionId, payload) =>
    opsClient
      .post(`/ops/sales-catalog/draft/${encodeURIComponent(versionId)}/products`, payload)
      .then((r) => r.data),

  // CLARTEXT · A1 (Feb 2026) — Aushangpflicht-Anzeige (Ops).
  aushangCurrent: () => opsClient.get("/ops/aushang/current").then((r) => r.data),
  // Preview: der Request-Body wird serverseitig verworfen (SSRF-Schutz);
  // Quell-URLs stammen ausschließlich aus dem aktiven Aushang-Datensatz.
  aushangPreview: () => opsClient.post("/ops/aushang/preview", {}).then((r) => r.data),
  // Apply erwartet den vollständigen zuvor angezeigten Vorschlag inkl. Hash.
  //
  // Aushang 2.0 · A6 (Feb 2026, Verbindungsauftrag): der Aufrufer darf
  // additiv `expected_staging_state_hash` mitgeben. Bleibt der Parameter
  // undefiniert (Legacy-A1-Aufruf), wird das Feld NICHT ins Request-Body
  // aufgenommen — der Legacy-Frontend-Vertrag bleibt byte-identisch.
  // Backend akzeptiert den Legacy-Aufruf weiterhin (Pydantic-Default "").
  aushangApply: ({
    expected_base_version_id,
    expected_proposal_hash,
    expected_staging_state_hash,
    proposal,
    reason,
  }) => {
    const body = {
      expected_base_version_id: expected_base_version_id ?? null,
      expected_proposal_hash,
      proposal,
      reason: reason || "",
    };
    if (expected_staging_state_hash !== undefined) {
      body.expected_staging_state_hash = expected_staging_state_hash;
    }
    return opsClient.post("/ops/aushang/apply", body).then((r) => r.data);
  },
  aushangHistory: () => opsClient.get("/ops/aushang/history").then((r) => r.data),

  // CLARTEXT · Aushang 2.0 · A5 (Feb 2026, Verbindungsauftrag) — Proposal
  // aus Staging (KEIN Legacy-Preview, KEIN Apply, KEIN Write).
  // Reine Anbindung an den bestehenden Backend-Endpunkt aus A5.
  aushangV2StagingProposal: () =>
    opsClient.post("/ops/aushang/staging/proposal", {}).then((r) => r.data),

  // CLARTEXT · Aushang 2.0 · A4 (2026-08-06) — Ops-Sektion „Aushangquellen".
  // REINE Anbindung an die bestehenden A2-Endpunkte in
  // `backend/routes/aushang_v2_ops.py` (GET/PUT/DELETE/POST). Kein neuer
  // Backend-Endpunkt. Kein Preview, kein Proposal, kein Apply.
  aushangV2Quellen: () => opsClient.get("/ops/aushang/quellen").then((r) => r.data),
  // Genau EINE Datei je Aufruf. Wir bauen das FormData hier auf, damit der
  // Browser den `multipart/form-data`-Content-Type inkl. Boundary setzt.
  aushangV2Upload: (sourceIdentifier, file) => {
    const form = new FormData();
    form.append("file", file);
    return opsClient
      .put(
        `/ops/aushang/quellen/${encodeURIComponent(sourceIdentifier)}/datei`,
        form,
      )
      .then((r) => r.data);
  },
  aushangV2Discard: (sourceIdentifier) =>
    opsClient
      .delete(`/ops/aushang/quellen/${encodeURIComponent(sourceIdentifier)}/datei`)
      .then((r) => r.data),
  aushangV2SetIntentionallyMissing: (sourceIdentifier, aktiv, begruendung) =>
    opsClient
      .post(
        `/ops/aushang/quellen/${encodeURIComponent(sourceIdentifier)}/bewusst-fehlend`,
        { aktiv: !!aktiv, begruendung: String(begruendung || "") },
      )
      .then((r) => r.data),

  // CLARTEXT · Block H Nachtrag (Feb 2026) — OPS-Center-UI für Modul-Add-ons.
  // Generische Anbindung: Registry-Liste + Status (beide read-only) sowie die
  // zwei bestehenden Block-H-Schreibrouten (Enable, Preis-Erst-Setzung mit
  // atomarer Abo-Anlage bei Betrag > 0). KEINE Preisänderungs-Route.
  moduleAddons: () => opsClient.get("/ops/module-addons").then((r) => r.data),
  moduleAddonStatus: (tid, moduleId) =>
    opsClient
      .get(`/ops/tenants/${tid}/module-addons/${encodeURIComponent(moduleId)}/status`)
      .then((r) => r.data),
  moduleAddonEnable: (tid, moduleId) =>
    opsClient
      .post(`/ops/tenants/${tid}/module-addons/${encodeURIComponent(moduleId)}/enable`)
      .then((r) => r.data),
  moduleAddonSetPrice: (tid, moduleId, amountNetCents) =>
    opsClient
      .post(`/ops/tenants/${tid}/module-addons/${encodeURIComponent(moduleId)}/price`, {
        amount_net_cents: amountNetCents,
      })
      .then((r) => r.data),

  // Kostenlos-Freigabe-Nachtrag (Feb 2026): Vertrags-Materialisierung aus dem
  // M2-Legacy-Mapping (AP-3-Präzedenzfall) — idempotent, kein Stripe-Objekt.
  contractMaterialize: (tid) =>
    opsClient.post(`/ops/tenants/${tid}/contract-materialize`).then((r) => r.data),

  // Deaktivierung (2026-06, F1-F8): Spiegel zur Enable-Route. Feste Regel:
  // Trial-Abo sofort, bezahltes Abo zum Periodenende (Zugriff bleibt bis dahin),
  // ohne Abo reine Entitlement-Entfernung. Produktgebündelt → 409 (F6).
  moduleAddonDisable: (tid, moduleId) =>
    opsClient
      .post(`/ops/tenants/${tid}/module-addons/${encodeURIComponent(moduleId)}/disable`)
      .then((r) => r.data),

  // ZAHL-01 (W1, Juni 2026) – Abrechnungs-Nachbearbeitung: READ der offenen/
  // erledigten Einträge (fehlgeschlagene Subscription-Kündigungen/Dubletten),
  // optional je Mandant/Status gefiltert. WRITE = „Kündigung erneut versuchen"
  // (idempotent; bereits gekündigt gilt als Erfolg). Reine Anbindung der
  // bestehenden OPS-Token-geschützten Endpunkte in `backend/routes/ops.py`.
  billingFollowups: (tenant, status) =>
    opsClient
      .get("/ops/billing/followups", {
        params: { ...(tenant ? { tenant } : {}), ...(status ? { status } : {}) },
      })
      .then((r) => r.data),
  billingFollowupRetry: (followupId) =>
    opsClient
      .post(`/ops/billing/followups/${encodeURIComponent(followupId)}/retry`)
      .then((r) => r.data),

  // ZAHL-02c (Betreiberfreigabe 13.10., K5) – Vertrag/Kündigung je Mandant: Vertragsdaten (read),
  // Vorschau des Wirksamkeitsdatums (read), Kündigung eintragen / zurücknehmen (write, OPS-Token).
  tenantVertrag: (tid) => opsClient.get(`/ops/tenants/${tid}/vertrag`).then((r) => r.data),
  kuendigungVorschau: (tid, eingangAm) =>
    opsClient
      .get(`/ops/tenants/${tid}/kuendigung/vorschau`, { params: eingangAm ? { eingang_am: eingangAm } : {} })
      .then((r) => r.data),
  kuendigungEintragen: (tid, { eingang_am, initiator, begruendung }) =>
    opsClient
      .post(`/ops/tenants/${tid}/kuendigung`, { eingang_am: eingang_am || null, initiator, begruendung })
      .then((r) => r.data),
  kuendigungZuruecknehmen: (tid, begruendung) =>
    opsClient.post(`/ops/tenants/${tid}/kuendigung/zuruecknehmen`, { begruendung }).then((r) => r.data),
  // ZAHL-02e (Betreiberfreigabe 14.10., E1/E2): Offene Vorgänge (read-only, paginiert) + Nachholen per Knopf.
  offeneVorgaenge: (tab, { offset = 0, limit = 50 } = {}) =>
    opsClient.get("/ops/offene-vorgaenge", { params: { tab, offset, limit } }).then((r) => r.data),
  abrechnungErneutVersuchen: (trueupId) =>
    opsClient.post(`/ops/offene-vorgaenge/${trueupId}/erneut-versuchen`).then((r) => r.data),
};

// Übersetzt einen axios-Fehler in eine verständliche, deutschsprachige Meldung.
export function opsErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 401) return "Token ungültig oder fehlt. Bitte erneut eingeben.";
  if (status === 503)
    return (
      (typeof detail === "string" && detail) ||
      "Betreiber-API ist serverseitig nicht konfiguriert (OPS_API_TOKEN fehlt)."
    );
  if (detail) return typeof detail === "string" ? detail : "Aktion fehlgeschlagen.";
  if (status) return `Fehler ${status}. Bitte erneut versuchen.`;
  return "Netzwerkfehler. Bitte Verbindung prüfen und erneut versuchen.";
}
